import * as React from 'react';
import { useState, useEffect } from 'react';

// Redux
import {
  useGetCurrentUserAccountStatesQuery,
  useUpdateUserAccountStatesMutation,
} from '@breakroom-nx/redux';

// Types
import { PermissionType, PermissionStatus } from '@breakroom-nx/helpers';

// Components
import { Image } from 'expo-image';
import { VStack, Heading, Text, HStack } from 'native-base';
import { PrimaryButton, LocalComponentError } from '../../';

// Assets
import { theme } from '@breakroom-nx/assets';
import { CheckIcon, XIcon } from 'react-native-heroicons/solid';

export interface PermissionPanelProps {
  type: PermissionType;
  title: string;
  description: string;
  primaryButtonText: string;
  image?: string;
  secondaryButtonText?: string;
}

const Enabled = () => {
  return (
    <HStack
      space={3}
      alignItems={'center'}
      margin={'auto'}
      borderWidth={'2'}
      borderColor={theme.colors.surface[400]}
      px={5}
      py={'14px'}
      rounded={'full'}
    >
      <CheckIcon size={17} color={theme.colors.primary[300]} />
      <Text color={theme.colors.primary[300]}>You're All Set!</Text>
    </HStack>
  );
};

const Disabled = () => {
  return (
    <HStack
      space={3}
      alignItems={'center'}
      margin={'auto'}
      borderWidth={'2'}
      borderColor={theme.colors.surface[400]}
      px={5}
      py={'14px'}
      rounded={'full'}
    >
      <XIcon size={17} color={theme.colors.red[200]} />
      <Text color={theme.colors.red[200]}>You Have Blocked this Service</Text>
    </HStack>
  );
};

export const PermissionPanel = ({
  type,
  title,
  description,
  primaryButtonText,
  image = undefined,
}: PermissionPanelProps) => {
  const { data: accountStates, isLoading: isLoadingAccountStates } =
    useGetCurrentUserAccountStatesQuery();
  const [updateAccountStates] = useUpdateUserAccountStatesMutation();

  const [textError, setTextError] = useState<string | undefined>(undefined);
  const [isWaitingOnUserInput, setIsWaitingOnUserInput] = useState(false);

  const serviceStatus = () => {
    if (accountStates) {
      switch (type) {
        case PermissionType.Cookies:
          return accountStates.cookiePermissions ?? PermissionStatus.NotSet;
        case PermissionType.Location:
          return accountStates.locationPermissions ?? PermissionStatus.NotSet;
        case PermissionType.Notifications:
          return (
            accountStates.notificationPermissions ?? PermissionStatus.NotSet
          );
      }
    } else {
      return PermissionStatus.NotSet;
    }
  };

  function checkGeolocation() {
    function success(position: any) {
      updateAccountStates({ locationPermissions: PermissionStatus.Enabled })
        .then(() => {
          setIsWaitingOnUserInput(false);
        })
        .catch((err) => {
          // console.log(`Error`);
          setTextError(`Error Setting Permission`);
        });
    }

    function error() {
      updateAccountStates({ locationPermissions: PermissionStatus.Disabled })
        .then(() => {
          setIsWaitingOnUserInput(false);
        })
        .catch((err) => {
          // console.log(`Error`);
          setTextError(`Error Setting Permission`);
        });
    }

    if (!navigator.geolocation) {
      updateAccountStates({ locationPermissions: PermissionStatus.Disabled })
        .then(() => {
          setIsWaitingOnUserInput(false);
        })
        .catch((err) => {
          // console.log(`Error`);
          setTextError(`Error Setting Permission`);
        });
    } else {
      navigator.geolocation.getCurrentPosition(success, error);
    }
  }

  function checkCookies() {
    updateAccountStates({ cookiePermissions: PermissionStatus.Enabled })
      .then(() => {
        setIsWaitingOnUserInput(false);
      })
      .catch((err) => {
        // console.log(`Error`);
        setTextError(`Error Setting Permission`);
      });
  }

  function checkNotifications() {
    function checkNotificationPromise() {
      try {
        Notification.requestPermission().then();
      } catch (e) {
        return false;
      }
      return true;
    }

    function handlePermission(permission: any) {
      // set the button to shown or hidden, depending on what the user answers
      const response = Notification.permission === 'granted';
      updateAccountStates({
        notificationPermissions: response
          ? PermissionStatus.Enabled
          : PermissionStatus.Disabled,
      })
        .then(() => {
          setIsWaitingOnUserInput(false);
        })
        .catch((err) => {
          // console.log(`Error`);
          setTextError(`Error Setting Permission`);
        });
    }

    // Let's check if the browser supports notifications
    if (!('Notification' in window)) {
      updateAccountStates({
        notificationPermissions: PermissionStatus.Disabled,
      })
        .then(() => {
          setIsWaitingOnUserInput(false);
        })
        .catch((err) => {
          // console.log(`Error`);
          setTextError(`Error Setting Permission`);
        });
    } else if (checkNotificationPromise()) {
      Notification.requestPermission().then((permission) => {
        handlePermission(permission);
      });
    } else {
      Notification.requestPermission((permission) => {
        handlePermission(permission);
      });
    }
  }

  const enablePermission = (type: PermissionType) => {
    setIsWaitingOnUserInput(true);

    switch (type) {
      case PermissionType.Cookies:
        checkCookies();
        break;
      case PermissionType.Location:
        checkGeolocation();
        break;
      case PermissionType.Notifications:
        checkNotifications();
        break;
      default:
        break;
    }
  };

  return (
    <VStack space={8} textAlign={'center'}>
      <LocalComponentError error={textError} />
      <Image
        source={{ uri: image }}
        cachePolicy={'memory-disk'}
        accessibilityLabel={`${title} Permissions Image`}
        style={{
          width: '90%',
          aspectRatio: 1,
        }}
      />

      <Heading fontSize={30} lineHeight={34}>
        {title}
      </Heading>
      <Text color={'surface.600'} fontSize={16}>
        {description}
      </Text>
      <VStack space={4} mb={5}>
        {serviceStatus() === PermissionStatus.NotSet ? (
          <PrimaryButton
            text={
              isWaitingOnUserInput
                ? 'Please Accept the Prompt'
                : primaryButtonText
            }
            disabled={isWaitingOnUserInput || isLoadingAccountStates}
            onPress={() => {
              enablePermission(type);
            }}
            bg={
              serviceStatus() === PermissionStatus.NotSet
                ? 'primary.400'
                : serviceStatus() === PermissionStatus.Enabled
                ? 'primary.400'
                : 'red.500'
            }
          />
        ) : serviceStatus() === PermissionStatus.Enabled ? (
          <Enabled />
        ) : (
          <Disabled />
        )}
      </VStack>
    </VStack>
  );
};
